import React, { useEffect, useRef, useState } from "react";
import Modal from "../elements/Modal";
import InputField from "../elements/InputField";
import Button from "../elements/Button";
import { authenticationService } from "../../api/authentication.service";
import Logo from "../../assets/images/logo.png";
import LoginImage from "../../assets/images/login-image.jpg";
import SignupImage from "../../assets/images/signup-image.jpg";
import { useRecaptchaV2 } from "../../hooks/useRecaptchaV2";

function AuthModal({
	isOpen,
	onClose,
	onAuthenticated,
	initialTab = "login",
	purpose = "pedigree",
}) {
	const [tab, setTab] = useState(initialTab);
	const dialogRef = useRef(null);
	const [errors, setErrors] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const {
		containerRef,
		token: recaptchaToken,
		reset: resetRecaptcha,
		isReady: isRecaptchaReady,
		recaptchaErrorOccurred,
	} = useRecaptchaV2();

	useEffect(() => {
		if (!isOpen) return;
		const previousFocus = document.activeElement;
		dialogRef.current?.querySelector("input")?.focus();
		function handleKey(event) {
			if (event.key === "Escape") {
				event.preventDefault();
				onClose();
			}
			if (event.key !== "Tab") return;
			const controls = Array.from(
				dialogRef.current.querySelectorAll(
					"button:not(:disabled), input, a[href], iframe",
				),
			);
			const first = controls[0],
				last = controls[controls.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		}
		document.addEventListener("keydown", handleKey);
		return () => {
			document.removeEventListener("keydown", handleKey);
			previousFocus?.focus();
		};
	}, [isOpen, onClose]);

	const handleSubmit = (e) => {
		e.preventDefault();
		setErrors({});
		setIsSubmitting(true);

		const formData = new FormData(e.target);
		if (tab === "signup") {
			if (!isRecaptchaReady || !recaptchaToken) {
				setIsSubmitting(false);
				return;
			}
			formData.append("captcha_token", recaptchaToken);
		}

		const authPromise =
			tab === "login"
				? authenticationService.login(formData)
				: authenticationService.signup(formData);

		authPromise
			.then((session) => {
				onAuthenticated(session);
			})
			.catch((error) => {
				setErrors({ ...error });
				if (tab === "signup") {
					resetRecaptcha();
				}
			})
			.finally(() => setIsSubmitting(false));
	};

	const isLogin = tab === "login";
	const sideImage = isLogin ? LoginImage : SignupImage;
	const title = isLogin ? (
		<>
			<span className="is-bold">welcome</span> back
		</>
	) : (
		<>
			<span className="is-bold">welcome</span>
		</>
	);
	const subtitle =
		purpose === "report"
			? isLogin
				? "Log in to unlock this shared report. You will stay on this page."
				: "Create your account to explore this shared report and compare horses."
			: isLogin
				? "Sign in to continue with your pedigree request without losing the horses you selected."
				: "Create your account to generate this pedigree report and keep your progress.";
	const isSignupDisabled = !isRecaptchaReady || !recaptchaToken;

	return (
		<Modal isOpen={isOpen} className="Modal__auth-container">
			<div
				className="auth-modal"
				role="dialog"
				aria-modal="true"
				aria-label={
					isLogin ? "Log in to ChampionBred" : "Sign up to ChampionBred"
				}
				ref={dialogRef}
			>
				<div className="auth-modal__panel auth-modal__panel--form">
					<div className="auth-modal__header">
						<img className="auth-modal__logo" src={Logo} alt="ChampionBred" />
						<button
							type="button"
							className="auth-modal__close"
							onClick={onClose}
							aria-label="Close authentication"
						>
							<i className="fa fa-times"></i>
						</button>
					</div>

					<div className="auth-modal__tabs">
						<button
							type="button"
							className={`auth-modal__tab ${isLogin ? "active" : ""}`}
							onClick={() => {
								setTab("login");
								setErrors({});
							}}
						>
							Log in
						</button>
						<button
							type="button"
							className={`auth-modal__tab ${!isLogin ? "active" : ""}`}
							onClick={() => {
								setTab("signup");
								setErrors({});
							}}
						>
							Sign up
						</button>
					</div>

					<div className="auth-modal__copy">
						<h1 className="huge-letters is-black line-margin-0">{title}</h1>
						<p className="subtext auth-modal__subtitle">{subtitle}</p>
					</div>

					<form
						className="auth-modal__form"
						aria-label={isLogin ? "Log in" : "Sign up"}
						onSubmit={handleSubmit}
					>
						{!isLogin && (
							<div className="auth-modal__row">
								<div className="auth-modal__field">
									<InputField
										name="first_name"
										id="auth-first-name"
										type="text"
										label="First Name"
										error={errors.first_name}
										class="signup-form-card__input"
									/>
								</div>
								<div className="auth-modal__field">
									<InputField
										name="last_name"
										id="auth-last-name"
										type="text"
										label="Last Name"
										error={errors.last_name}
										class="signup-form-card__input"
									/>
								</div>
							</div>
						)}
						<InputField
							name="email"
							id="auth-email"
							type="email"
							label="Email"
							error={errors.email}
							class="signup-form-card__input"
						/>
						<InputField
							name="password"
							id="auth-password"
							type="password"
							label="Password"
							error={
								errors.password || errors.non_field_errors || errors.detail
							}
							class="signup-form-card__input"
						/>
						{!isLogin && (
							<>
								<InputField
									name="password2"
									id="auth-repeat-password"
									type="password"
									label="Repeat Password"
									error={
										errors.password2 || errors.non_field_errors || errors.detail
									}
									class="signup-form-card__input"
								/>
								<div className="signup-form-card__recaptcha-wrapper auth-modal__recaptcha">
									<div ref={containerRef} />
								</div>
								{recaptchaErrorOccurred ? (
									<div className="left input-field signup-form-card__input auth-modal__recaptcha-error">
										<p className="error">
											Unknown error. Please try again or contact{" "}
											<span style={{ textDecoration: "underline" }}>
												info@championbred.com.au
											</span>
										</p>
									</div>
								) : null}
							</>
						)}
						{isLogin ? (
							<p className="line-margin-0 right auth-modal__helper">
								<a className="subtext link is-gold" href="/forgot-password">
									Forgot password?
								</a>
							</p>
						) : null}
						<Button
							text={isLogin ? "Sign in" : "Sign up"}
							type="submit"
							class="full-width signup-form-card__submit auth-modal__submit"
							inactive={isSubmitting || (!isLogin && isSignupDisabled)}
						/>
					</form>

					<p className="subtext is-black auth-modal__footer">
						{isLogin ? "New user?" : "Already have an account?"}&nbsp;
						<button
							type="button"
							className="auth-modal__footer-link"
							onClick={() => {
								setTab(isLogin ? "signup" : "login");
								setErrors({});
							}}
						>
							{isLogin ? "Sign Up" : "Log In"}
						</button>
					</p>
				</div>
				<div className="auth-modal__panel auth-modal__panel--image">
					<img src={sideImage} alt="ChampionBred authentication" />
				</div>
			</div>
		</Modal>
	);
}

export default AuthModal;
